import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { firestore, FormikRadioButton, Subtitle1, useTheme, VectorIcon } from "capsule"
import firebase from "firebase/compat/app"
import { Formik, useFormikContext } from "formik"
import React, { useMemo } from "react"
import { useCollectionData } from "react-firebase-hooks/firestore"
import { useTranslation } from "react-i18next"
import { FlatList, Platform, StyleSheet, View } from "react-native"
import FastImage from "react-native-fast-image"

import { Organ } from "../../common/Pathology"
import { collections } from "../../common/types"
import { INPUT_HEIGHT } from "../../features/config/Constants"
import { itemName } from "../../features/hooks/useItemI18n"
import { NewClientOnboardingNS } from "../../features/i18n/constants"
import { NewClientOnboardingParamList } from "../../features/Navigation/types"
import { getNewClientDocId, updateNewClientData } from "../../features/services/NewClientService"

const NewClientOrganSelectScreen = () => {
  const { t } = useTranslation(NewClientOnboardingNS)
  const navigation =
    useNavigation<StackNavigationProp<NewClientOnboardingParamList, "NewClientOrganSelect">>()

  const parentFormik = useFormikContext<{ organ?: string }>()

  const {
    colors: {
      surface: { background },
      black: { highEmphasis: black },
      white: { highEmphasis: white },
    },
    dimensions: { spacing },
  } = useTheme()

  const [organs] = useCollectionData<Organ>(
    firestore()
      .collection(collections.ORGANS)
      .where("specialty", "==", "ortho")
      .where("disabled", "==", false) as unknown as firebase.firestore.Query<Organ>,
    { idField: "id" },
  )

  const s = useMemo(
    () => ({
      container: [
        styles.container,
        {
          backgroundColor: background,
        },
      ],
      contentContainer: {
        backgroundColor: background,
        paddingVertical: spacing * 2,
      },
      radioButton: [
        styles.radioButton,
        {
          backgroundColor: white,
          paddingHorizontal: spacing,
          marginHorizontal: spacing * 2,
          marginBottom: spacing,
        },
      ],
      label: [
        styles.label,
        {
          paddingLeft: spacing / 2,
        },
      ],
      image: [
        styles.image,
        {
          margin: spacing,
          marginRight: spacing * 2,
        },
      ],
      text: {
        color: black,
      },
    }),
    [background, spacing, white, black],
  )

  const onSubmit = async (values: { organ: string }) => {
    parentFormik.setFieldValue("organ", values.organ)

    const docId = await getNewClientDocId()
    await updateNewClientData(docId, {
      organ: values.organ,
      specialty: "ortho",
    })

    navigation.navigate("NewClientPathology", {
      organId: values.organ,
      specialty: "ortho",
    })
  }

  const renderItem = ({ item, setFieldValue, handleSubmit }) => (
    <FormikRadioButton
      name="organ"
      value={item.id}
      textOnly={false}
      onPress={() => {
        setFieldValue("organ", item.id)
        setTimeout(() => handleSubmit(), 100)
      }}
      renderLabel={() => (
        <View style={s.label}>
          {item.image ? (
            <FastImage
              style={s.image}
              source={{
                uri: item.image,
                ...(Platform.OS !== "web" && {
                  priority: FastImage.priority.high,
                  cache: FastImage.cacheControl.immutable,
                }),
              }}
              {...(Platform.OS !== "web" && {
                resizeMode: FastImage.resizeMode.cover,
              })}
            />
          ) : (
            <View style={s.image}>
              <VectorIcon name="photo" category="MaterialIcons" size={50} />
            </View>
          )}
          <Subtitle1
            style={[styles.text, s.text]}
            numberOfLines={2}
            ellipsizeMode="tail"
            maxFontSizeMultiplier={1}
          >
            {itemName(item, t)}
          </Subtitle1>
        </View>
      )}
      flexDirection="row"
      touchableStyle={s.radioButton}
      testID={`radio_organ_${item.id}`}
    />
  )

  return (
    <Formik initialValues={{ organ: "" }} onSubmit={onSubmit}>
      {({ setFieldValue, handleSubmit }) => (
        <FlatList
          style={s.container}
          data={organs || []}
          keyExtractor={item => item.id}
          renderItem={({ item }) => renderItem({ item, setFieldValue, handleSubmit })}
          contentContainerStyle={s.contentContainer}
        />
      )}
    </Formik>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  image: {
    height: INPUT_HEIGHT,
    width: INPUT_HEIGHT,
  },
  label: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
  },
  radioButton: {
    height: 80,
    alignItems: "center",
    alignSelf: "center",
    justifyContent: "center",
  },
  text: {
    flex: 1,
  },
})

export default NewClientOrganSelectScreen
